import type { components } from './schema';

type Problema = components['schemas']['Problema'];

/** Códigos do backend mais os do próprio cliente (rede e resposta fora do padrão). */
export type CodigoDeErro = Problema['code'] | 'SEM_CONEXAO' | 'RESPOSTA_INVALIDA';

export interface ErroDeCampo {
  path: string;
  message: string;
}

interface DadosDoErro {
  status: number;
  code: CodigoDeErro;
  detail?: string | undefined;
  errors?: readonly ErroDeCampo[] | undefined;
  motivo?: string | undefined;
  authCode?: string | undefined;
  requestId?: string | undefined;
}

/**
 * Erro único do painel para qualquer falha de chamada à API (docs/02, "Erros"). `status` é 0
 * quando a resposta nem chegou (rede ou tempo esgotado).
 */
export class ErroDaApi extends Error {
  readonly status: number;
  readonly code: CodigoDeErro;
  readonly detail: string | undefined;
  readonly errors: readonly ErroDeCampo[];
  readonly motivo: string | undefined;
  readonly authCode: string | undefined;
  readonly requestId: string | undefined;

  constructor(dados: DadosDoErro, opcoes?: ErrorOptions) {
    super(dados.detail ?? dados.code, opcoes);
    this.name = 'ErroDaApi';
    this.status = dados.status;
    this.code = dados.code;
    this.detail = dados.detail;
    this.errors = dados.errors ?? [];
    this.motivo = dados.motivo;
    this.authCode = dados.authCode;
    this.requestId = dados.requestId;
  }

  /** Falha antes de qualquer resposta: rede, servidor fora do ar ou 30 s sem resposta. */
  get semResposta(): boolean {
    return this.status === 0;
  }
}

export function eErroDaApi(erro: unknown): erro is ErroDaApi {
  return erro instanceof ErroDaApi;
}

const CODIGOS_DO_BACKEND: ReadonlySet<string> = new Set<Problema['code']>([
  'VALIDACAO',
  'NAO_AUTENTICADO',
  'SEM_PERMISSAO',
  'NAO_ENCONTRADO',
  'PAGAMENTO_RECUSADO',
  'CONFLITO',
  'ENCERRADO',
  'CORPO_MUITO_GRANDE',
  'LIMITE_EXCEDIDO',
  'ERRO_INTERNO',
  'INDISPONIVEL',
]);

function eCodigoDoBackend(valor: string | undefined): valor is Problema['code'] {
  return valor !== undefined && CODIGOS_DO_BACKEND.has(valor);
}

function texto(valor: unknown): string | undefined {
  return typeof valor === 'string' && valor.length > 0 ? valor : undefined;
}

function errosDeCampo(valor: unknown): ErroDeCampo[] {
  if (!Array.isArray(valor)) return [];
  return valor.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return [];
    const { path, message } = item as Record<string, unknown>;
    return typeof path === 'string' && typeof message === 'string' ? [{ path, message }] : [];
  });
}

/** Código pelo status, para respostas sem `code` (ex. proxy devolvendo 502 em HTML). */
function codigoPeloStatus(status: number): CodigoDeErro {
  if (status === 400 || status === 422) return 'VALIDACAO';
  if (status === 401) return 'NAO_AUTENTICADO';
  if (status === 403) return 'SEM_PERMISSAO';
  if (status === 404) return 'NAO_ENCONTRADO';
  if (status === 409) return 'CONFLITO';
  if (status === 413) return 'CORPO_MUITO_GRANDE';
  if (status === 429) return 'LIMITE_EXCEDIDO';
  if (status === 502 || status === 503 || status === 504) return 'INDISPONIVEL';
  if (status >= 500) return 'ERRO_INTERNO';
  return 'RESPOSTA_INVALIDA';
}

/** Converte uma resposta que não é 2xx (de preferência `problem+json`) em `ErroDaApi` (CA-04). */
export async function erroDaResposta(resposta: Response): Promise<ErroDaApi> {
  let corpo: Record<string, unknown> = {};
  try {
    const lido: unknown = await resposta.clone().json();
    if (typeof lido === 'object' && lido !== null) corpo = lido as Record<string, unknown>;
  } catch {
    // Corpo vazio ou que não é JSON: fica só com o status.
  }
  const code = texto(corpo.code);
  return new ErroDaApi({
    status: resposta.status,
    code: eCodigoDoBackend(code) ? code : codigoPeloStatus(resposta.status),
    detail: texto(corpo.detail),
    errors: errosDeCampo(corpo.errors),
    motivo: texto(corpo.motivo),
    authCode: texto(corpo.authCode),
    requestId: texto(corpo.requestId) ?? resposta.headers.get('x-request-id') ?? undefined,
  });
}

/** Falha sem resposta (rede, servidor fora do ar, tempo esgotado) → `SEM_CONEXAO` (CA-05). */
export function erroSemConexao(causa: unknown, requestId?: string): ErroDaApi {
  return new ErroDaApi({ status: 0, code: 'SEM_CONEXAO', requestId }, { cause: causa });
}

const MENSAGENS: Record<CodigoDeErro, string> = {
  VALIDACAO: 'Confira os dados informados.',
  NAO_AUTENTICADO: 'Sua sessão expirou. Entre de novo.',
  SEM_PERMISSAO: 'Você não tem permissão para isso.',
  NAO_ENCONTRADO: 'Não encontrado.',
  PAGAMENTO_RECUSADO: 'Pagamento não aprovado. Confira os dados do cartão ou use outro.',
  CONFLITO: 'Já existe um registro com estes dados.',
  ENCERRADO: 'Isto não está mais disponível.',
  CORPO_MUITO_GRANDE: 'O envio é grande demais.',
  LIMITE_EXCEDIDO: 'Muitas tentativas. Aguarde um pouco e tente de novo.',
  ERRO_INTERNO: 'Não foi possível concluir. Tente de novo.',
  INDISPONIVEL: 'Não foi possível concluir. Tente de novo.',
  SEM_CONEXAO: 'Sem conexão. Verifique a internet e tente de novo.',
  RESPOSTA_INVALIDA: 'Não foi possível concluir. Tente de novo.',
};

/**
 * Texto para a pessoa. Mensagem técnica nunca aparece (docs/02): o `detail` do backend, que já vem
 * em pt-BR, só é usado nos códigos em que ele é escrito para o usuário e nos erros do Better Auth
 * (com `authCode`), que o backend traduz.
 */
export function mensagemPara(erro: unknown): string {
  if (!eErroDaApi(erro)) return MENSAGENS.ERRO_INTERNO;
  const detalheParaUsuario =
    erro.code === 'VALIDACAO' ||
    erro.code === 'CONFLITO' ||
    erro.code === 'ENCERRADO' ||
    erro.code === 'PAGAMENTO_RECUSADO' ||
    erro.code === 'SEM_PERMISSAO' ||
    (erro.authCode !== undefined && erro.status < 500);
  if (detalheParaUsuario && erro.detail !== undefined) return erro.detail;
  return MENSAGENS[erro.code];
}
